import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Clock, MapPin, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { SEO, SITE_URL } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT = {
  email: "hello@travelclubnavigator.co",
  phone: "+1 (888) 555-0142",
  phoneHref: "tel:+18885550142",
  hours: [
    { day: "Monday – Friday", time: "9:00 AM – 6:00 PM ET" },
    { day: "Saturday", time: "10:00 AM – 2:00 PM ET" },
    { day: "Sunday", time: "Closed" },
  ],
  location: "Serving travelers across the United States",
};

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1503220317375-aaad61436b1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        package_name: "CONTACT PAGE",
        message: form.message,
      });
      setDone(true);
      toast.success("Message sent! Our team will reply within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact Travel Club Navigator Group",
    url: `${SITE_URL}/contact`,
    mainEntity: {
      "@type": "Organization",
      name: "Travel Club Navigator Group",
      email: CONTACT.email,
      telephone: CONTACT.phone,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: CONTACT.email,
        telephone: CONTACT.phone,
        areaServed: "US",
        availableLanguage: "English",
      },
    },
  };

  return (
    <>
      <SEO
        title="Contact Our Team — Questions, Bookings & Support"
        description="Get in touch with Travel Club Navigator Group. Ask about a vacation package, a travel club review, or your membership — our team replies within 24 hours."
        path="/contact"
        keywords={["Contact Travel Club Navigator", "travel club help", "vacation package inquiry", "timeshare questions"]}
        jsonLd={jsonLd}
        image={HERO_IMAGE}
      />

      {/* HERO */}
      <section className="relative flex min-h-[46vh] items-end overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Traveler looking out over a sunlit coastline, ready to plan a getaway"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
            data-testid="contact-hero"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-white">
              <MessageCircle className="h-4 w-4" /> We're Here to Help
            </span>
            <h1 className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Let's talk travel — no pressure, no scripts.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-200">
              Questions about a package, a review, or your membership? Send us a note and a
              real human on our team will get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          {/* Info column */}
          <div className="lg:col-span-2">
            <Reveal>
              <Overline>Reach Us Directly</Overline>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy">
                Prefer a quick call or email?
              </h2>
              <p className="mt-3 leading-relaxed text-slate-600">
                We keep it human. Use whatever's easiest for you — every message reaches our team,
                not a bot.
              </p>
            </Reveal>

            <div className="mt-9 space-y-4">
              <Reveal delay={0.05}>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="contact-email-link"
                  className="flex items-start gap-4 rounded-2xl card-3d p-5 transition-transform duration-200 hover:-translate-y-0.5"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-navy">Email</span>
                    <span className="block text-sm text-slate-600">{CONTACT.email}</span>
                  </span>
                </a>
              </Reveal>

              <Reveal delay={0.1}>
                <a
                  href={CONTACT.phoneHref}
                  data-testid="contact-phone-link"
                  className="flex items-start gap-4 rounded-2xl card-3d p-5 transition-transform duration-200 hover:-translate-y-0.5"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                    <Phone className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-navy">Phone</span>
                    <span className="block text-sm text-slate-600">{CONTACT.phone}</span>
                  </span>
                </a>
              </Reveal>

              <Reveal delay={0.15}>
                <div
                  data-testid="contact-hours"
                  className="flex items-start gap-4 rounded-2xl card-3d p-5"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
                    <Clock className="h-5 w-5" />
                  </span>
                  <div className="w-full">
                    <span className="block text-sm font-semibold text-navy">Business Hours</span>
                    <ul className="mt-1.5 space-y-1">
                      {CONTACT.hours.map((h) => (
                        <li key={h.day} className="flex justify-between gap-4 text-sm text-slate-600">
                          <span>{h.day}</span>
                          <span className="font-medium text-navy">{h.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="flex items-start gap-4 rounded-2xl card-3d p-5">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-navy">Where We Serve</span>
                    <span className="block text-sm text-slate-600">{CONTACT.location}</span>
                  </span>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-3">
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-[2rem] card-3d">
                <div className="bg-navy px-8 py-8">
                  <h2 className="font-display text-2xl font-bold text-white">Send us a message</h2>
                  <p className="mt-2 text-slate-300">
                    Tell us what you need — bookings, questions, or feedback. We reply within 24 hours.
                  </p>
                </div>
                {done ? (
                  <div
                    data-testid="contact-success"
                    className="flex flex-col items-center px-8 py-16 text-center"
                  >
                    <CheckCircle2 className="h-16 w-16 text-secondary" />
                    <h3 className="mt-5 font-display text-2xl font-bold text-navy">Message received!</h3>
                    <p className="mt-2 max-w-sm text-slate-500">
                      Thanks, {form.name.split(" ")[0] || "there"} — a Navigator specialist will get
                      back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4 px-8 py-8" data-testid="contact-form">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Full name" required>
                        <Input
                          data-testid="contact-name-input"
                          value={form.name}
                          onChange={update("name")}
                          placeholder="Jordan Rivera"
                          className="rounded-xl"
                        />
                      </Field>
                      <Field label="Email" required>
                        <Input
                          data-testid="contact-email-input"
                          type="email"
                          value={form.email}
                          onChange={update("email")}
                          placeholder="you@email.com"
                          className="rounded-xl"
                        />
                      </Field>
                    </div>
                    <Field label="Phone">
                      <Input
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(555) 123-4567"
                        className="rounded-xl"
                      />
                    </Field>
                    <Field label="How can we help?" required>
                      <Textarea
                        data-testid="contact-message-input"
                        value={form.message}
                        onChange={update("message")}
                        placeholder="Ask about a package, a review, your membership, or anything else..."
                        className="min-h-[130px] rounded-xl"
                      />
                    </Field>
                    <Button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={submitting}
                      className="w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground btn-glow hover:bg-primary/90"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                        </>
                      ) : (
                        "Send Message"
                      )}
                    </Button>
                    <p className="text-center text-xs text-slate-400">
                      No spam, no sales scripts. Just a transparent, human reply.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-navy">
      {label}
      {required && <span className="text-primary"> *</span>}
    </Label>
    {children}
  </div>
);
